import React, { useState, useEffect } from "react";
import CustomLoader from "../ui/custom-loader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useToast } from "../../components/ui/use-toast";
import { services } from "../../services";
import { actions } from "../../actions";
import { web3 } from "../../web3";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { NFTRecord } from "../../types/nft/nfts";

interface ResellPriceProps {
  record: NFTRecord;
}

const ResellPrice: React.FC<ResellPriceProps> = ({ record }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [priceTransactionLoading, setPriceTransactionLoading] = useState(false);
  const [minPricePercent, setMinPricePercent] = useState<string>("");
  const [maxPricePercent, setMaxPricePercent] = useState<string>("");
  const [priceValue, setPriceValue] = useState<string>("");
  const [currentMinPercent, setCurrentMinPercent] = useState<string>("0");
  const [currentMaxPercent, setCurrentMaxPercent] = useState<string>("0");
  const [currentPrice, setCurrentPrice] = useState<string>("0");
  const [web3Data, setWeb3Data] = useState(null);
  const [resellPoolContract, setResellPoolContract] = useState(null);
  const [assetImplimentationContract, setAssetImplimentationContract] =
    useState(null);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    async function initializeWeb3AndCheckRegistration() {
      try {
        setLoading(true);
        const web3Data = await services.getWeb3();
        if (!web3Data || !web3Data.accounts || !web3Data.accounts[0]) {
          throw new Error("Please connect your wallet first");
        }
        setWeb3Data(web3Data);

        const resellPoolInstance = await actions.getResellPoolContract();
        if (!resellPoolInstance) {
          throw new Error("Failed to initialize ResellPool contract");
        }
        setResellPoolContract(resellPoolInstance);

        // Initialize asset implementation contract
        if (record?.assetAddress) {
          const contractInstance = await actions.getPropertyAssetContract(
            record.assetAddress,
          );
          if (!contractInstance) {
            throw new Error("Failed to initialize asset contract");
          }
          setAssetImplimentationContract(contractInstance);

          // Get current price
          const priceResult: string = await contractInstance.methods
            .assetPrice()
            .call();
          setCurrentPrice(web3.utils.fromWei(priceResult.toString(), "ether"));
        }

        // Check if property is registered
        if (record?.assetAddress) {
          const isPropertyReg: boolean = await resellPoolInstance.methods
            .isPropertyRegistered(record.assetAddress)
            .call();
          setIsRegistered(isPropertyReg);
        }

        setLoading(false);
      } catch (error) {
        toast({
          description: "Failed to initialize: " + error.message,
          variant: "destructive",
        });
        setLoading(false);
      }
    }

    initializeWeb3AndCheckRegistration();
  }, [toast, record?.assetAddress]);

  useEffect(() => {
    async function fetchResellPrices() {
      if (!resellPoolContract || !record?.assetAddress || !isRegistered) return;

      try {
        const minPercent: string = await resellPoolContract.methods
          .minResellPricePercent(record.assetAddress)
          .call();
        const maxPercent: string = await resellPoolContract.methods
          .maxResellPricePercent(record.assetAddress)
          .call();

        setCurrentMinPercent((Number(minPercent) / 100).toString());
        setCurrentMaxPercent((Number(maxPercent) / 100).toString());
      } catch (error) {
        toast({
          description: "Failed to fetch resell prices: " + error.message,
          variant: "destructive",
        });
      }
    }

    if (open) {
      fetchResellPrices();
    }
  }, [resellPoolContract, record?.assetAddress, open, toast, isRegistered]);

  const handleMinPercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMinPricePercent(value);
  };

  const handleMaxPercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMaxPricePercent(value);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceValue(value);
  };

  const updatePrice = async () => {
    const numericValue = parseFloat(priceValue);

    if (isNaN(numericValue) || numericValue <= 0) {
      toast({
        title: "Error",
        description: "Please enter a valid positive number",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: "Error",
          description: "Please connect your wallet first.",
          variant: "destructive",
        });
        return;
      }

      if (!assetImplimentationContract) {
        toast({
          title: "Error",
          description:
            "Contract not initialized. Please check your wallet connection.",
          variant: "destructive",
        });
        return;
      }

      setPriceTransactionLoading(true);
      const priceInWei = web3.utils.toWei(priceValue.toString(), "ether");

      await assetImplimentationContract.methods
        .updateAssetPrice(priceInWei)
        .send({ from: web3Data.accounts[0] })
        .on("transactionHash", (hash) => {
          toast({
            title: "Transaction Initiated",
            description: `Your price update transaction has been submitted. Transaction hash: ${hash.slice(0, 10)}...`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("receipt", async () => {
          setPriceValue("");
          const newPrice: string = await assetImplimentationContract.methods
            .assetPrice()
            .call();
          setCurrentPrice(web3.utils.fromWei(newPrice.toString(), "ether"));
          setPriceTransactionLoading(false);
          toast({
            title: "Success",
            description: `Price has been successfully updated to ${priceValue} USDT`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("error", (error) => {
          setPriceTransactionLoading(false);
          toast({
            title: "Transaction Failed",
            description:
              error.message || "Failed to update price. Please try again.",
            variant: "destructive",
            duration: 5000,
          });
        });
    } catch (error) {
      setPriceTransactionLoading(false);
      toast({
        title: "Error",
        description:
          "Failed to update price: " +
          (error.message || "Unknown error. Please try again."),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const setResellPriceLimits = async () => {
    const minPercentValue = parseFloat(minPricePercent) * 100;
    const maxPercentValue = parseFloat(maxPricePercent) * 100;

    if (minPercentValue < 0 || maxPercentValue < 0) {
      toast({
        title: "Error",
        description: "Percentages must be greater than or equal to 0",
        variant: "destructive",
      });
      return;
    }

    if (minPercentValue >= maxPercentValue) {
      toast({
        title: "Error",
        description: "Minimum percentage must be less than maximum percentage",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: "Error",
          description: "Please connect your wallet first.",
          variant: "destructive",
        });
        return;
      }

      if (!resellPoolContract) {
        toast({
          title: "Error",
          description:
            "Contract not initialized. Please check your wallet connection.",
          variant: "destructive",
        });
        return;
      }

      if (!record?.assetAddress) {
        toast({
          title: "Error",
          description: "Asset address not found.",
          variant: "destructive",
        });
        return;
      }

      if (!isRegistered) {
        toast({
          title: "Error",
          description: "This property is not registered for resell.",
          variant: "destructive",
        });
        return;
      }

      setTransactionLoading(true);

      await resellPoolContract.methods
        .setResellPriceLimits(
          record.assetAddress,
          Math.floor(minPercentValue),
          Math.floor(maxPercentValue),
        )
        .send({ from: web3Data.accounts[0] })
        .on("transactionHash", (hash) => {
          toast({
            title: "Transaction Initiated",
            description: `Your transaction has been submitted. Transaction hash: ${hash.slice(0, 10)}...`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("receipt", async () => {
          setMinPricePercent("");
          setMaxPricePercent("");

          // Update current percentages
          const minPercent = await resellPoolContract.methods
            .minResellPricePercent(record.assetAddress)
            .call();
          const maxPercent = await resellPoolContract.methods
            .maxResellPricePercent(record.assetAddress)
            .call();
          setCurrentMinPercent((Number(minPercent) / 100).toString());
          setCurrentMaxPercent((Number(maxPercent) / 100).toString());

          setTransactionLoading(false);
          setOpen(false);
          toast({
            title: "Success",
            description: `Resell price limits have been successfully updated`,
            variant: "default",
            duration: 5000,
          });
        })
        .on("error", (error) => {
          setTransactionLoading(false);
          toast({
            title: "Transaction Failed",
            description:
              error.message ||
              "Failed to update resell price limits. Please try again.",
            variant: "destructive",
            duration: 5000,
          });
        });
    } catch (error) {
      setTransactionLoading(false);
      toast({
        title: "Error",
        description:
          "Failed to update resell price limits: " +
          (error.message || "Unknown error. Please try again."),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <CustomLoader size="sm" />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={`${isRegistered ? "bg-background hover:bg-primary/90" : "bg-background opacity-50 hover:bg-primary cursor-not-allowed"} text-black`}
          disabled={!isRegistered}
          title={!isRegistered ? "Property not registered for resell" : ""}
        >
          Configure Prices
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Set Resell Price Limits for {record.assetName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex flex-col space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Current Asset Price:
              </span>
              <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded-md">
                {parseFloat(currentPrice).toFixed(2)} USDT
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Current Min Price Percent:
              </span>
              <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded-md">
                {currentMinPercent}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Current Max Price Percent:
              </span>
              <span className="text-sm font-semibold text-primary bg-primary/10 px-2 py-1 rounded-md">
                {currentMaxPercent}%
              </span>
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Appreciate/Depreciate Asset Price (USDT)
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter new price in USDT"
                type="number"
                min={0}
                value={priceValue}
                onChange={handlePriceChange}
                disabled={priceTransactionLoading}
                className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
              />
              <Button
                variant="default"
                onClick={updatePrice}
                disabled={priceTransactionLoading}
                className="bg-primary hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90"
              >
                {priceTransactionLoading ? (
                  <div className="flex items-center gap-2">
                    <CustomLoader size="sm" className="h-4 w-4 animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Minimum Price Percent
            </label>
            <Input
              placeholder="Enter minimum percentage"
              type="number"
              min={0}
              value={minPricePercent}
              onChange={handleMinPercentChange}
              disabled={transactionLoading}
              className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
            />
          </div>
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Maximum Price Percent
            </label>
            <Input
              placeholder="Enter maximum percentage"
              type="number"
              min={0}
              value={maxPricePercent}
              onChange={handleMaxPercentChange}
              disabled={transactionLoading}
              className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
            />
          </div>
          <div className="pt-4">
            <Button
              variant="default"
              onClick={setResellPriceLimits}
              disabled={transactionLoading}
              className="w-full bg-primary hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90"
            >
              {transactionLoading ? (
                <div className="flex items-center gap-2">
                  <CustomLoader size="sm" className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </div>
              ) : (
                "Set Resell Price Limits"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResellPrice;
